import { createSlice } from '@reduxjs/toolkit';

// Undo/Redo history for the page Design. Holds full snapshots of the `design`
// slice (the same shape `designRestore` consumes). The history middleware pushes
// the pre-mutation design onto `past` before every design-changing action; the
// undo/redo thunks move snapshots between `past`/`future` and re-apply them.
//
// In-memory only — a page reload clears the stack by design. Server-side design
// checkpoints (DesignHistory) remain the heavy, cross-reload fallback.
const HISTORY_LIMIT = 50;

export const designHistorySlice = createSlice({
  name: 'designHistory',
  initialState: {
    past: [],
    future: [],
    limit: HISTORY_LIMIT,
    // Recording is only on while the Design editor is open. Kept in the store
    // (not a module variable) so the middleware and the UI always read the same
    // value regardless of how the bundler splits chunks.
    enabled: false,
  },
  reducers: {
    designHistorySetEnabled: (state, { payload }) => {
      state.enabled = !!payload;
    },
    // Record a pre-mutation snapshot. Any new edit invalidates the redo stack.
    designHistoryRecord: (state, { payload }) => {
      state.past.push(payload);
      if (state.past.length > state.limit) state.past.shift();
      state.future = [];
    },
    // Undo: caller supplies the current snapshot to stash into `future`; we drop
    // the top of `past` (which the thunk restores).
    designHistoryUndo: (state, { payload: current }) => {
      if (!state.past.length) return;
      state.future.push(current);
      if (state.future.length > state.limit) state.future.shift();
      state.past.pop();
    },
    // Redo: mirror of undo.
    designHistoryRedo: (state, { payload: current }) => {
      if (!state.future.length) return;
      state.past.push(current);
      if (state.past.length > state.limit) state.past.shift();
      state.future.pop();
    },
    designHistoryClear: (state) => {
      state.past = [];
      state.future = [];
    },
  },
});

export const { designHistorySetEnabled, designHistoryRecord, designHistoryUndo, designHistoryRedo, designHistoryClear } = designHistorySlice.actions;

export const selectCanUndo = (state) => state.designHistory.past.length > 0;
export const selectCanRedo = (state) => state.designHistory.future.length > 0;

export default designHistorySlice.reducer;
